'use client';

import { motion } from 'framer-motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { HelpCircle } from 'lucide-react';
import { useTranslation } from "@/hooks/useTranslation";


export default function FAQSection() {
    const { t, isRTL } = useTranslation("all-on-4");

    const faqs = [
        {
            id: 'what-is-all-on-4',
            question: t("faq.questions.whatIs.question"),
            answer: t("faq.questions.whatIs.answer")
        },
        {
            id: 'same-day-teeth',
            question: t("faq.questions.sameDayTeeth.question"),
            answer: t("faq.questions.sameDayTeeth.answer")
        },
        {
            id: 'duration',
            question: t("faq.questions.duration.question"),
            answer: t("faq.questions.duration.answer")
        },
        {
            id: 'vs-dentures',
            question: t("faq.questions.vsDentures.question"),
            answer: t("faq.questions.vsDentures.answer")
        },
        {
            id: 'success-rate',
            question: t("faq.questions.successRate.question"),
            answer: t("faq.questions.successRate.answer")
        },
        {
            id: 'eating-restrictions',
            question: t("faq.questions.eatingRestrictions.question"),
            answer: t("faq.questions.eatingRestrictions.answer")
        },
        {
            id: 'maintenance',
            question: t("faq.questions.maintenance.question"),
            answer: t("faq.questions.maintenance.answer")
        },
        {
            id: 'travel-istanbul',
            question: t("faq.questions.travelIstanbul.question"),
            answer: t("faq.questions.travelIstanbul.answer")
        }
    ];

    return (
        <section
            id="faq"
            className="py-12 lg:py-16 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gold/10 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-12">
                    <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif">
                        {t("faq.title.part1")}{' '}
                        <span className="text-gold relative">
                            {t("faq.title.highlight")}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
                        {t("faq.subtitle")}
                    </p>
                </div>

                {/* FAQ Accordion */}
                <div className="max-w-4xl mx-auto">
                    <Accordion type="single" collapsible className="space-y-3">
                        {faqs.map((faq, index) => (
                            <motion.div
                                key={faq.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <AccordionItem
                                    value={faq.id}
                                    className="bg-slate-50 dark:bg-navy-950 border-slate-200 dark:border-slate-700 rounded-2xl px-6 py-2 data-[state=open]:border-gold/50 transition-all duration-300 group overflow-hidden"
                                >
                                    <AccordionTrigger className={`${isRTL ? 'text-right' : 'text-left'} hover:no-underline py-6 group-data-[state=open]:text-gold dark:group-data-[state=open]:text-gold transition-colors duration-300`}>
                                        <div className={`flex items-start gap-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                                            <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center shrink-0 mt-1 group-data-[state=open]:scale-110 transition-transform duration-300">
                                                <HelpCircle className="w-4 h-4 text-white" />
                                            </div>
                                            <span className={`text-lg md:text-xl font-bold text-navy dark:text-white leading-relaxed ${isRTL ? 'text-right' : ''}`}>
                                                {faq.question}
                                            </span>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className={`text-slate-600 dark:text-slate-300 text-lg leading-relaxed pb-6 ${isRTL ? 'pr-12 text-right' : 'pl-12'}`}>
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.3, delay: 0.1 }}
                                        >
                                            {faq.answer}
                                        </motion.div>
                                    </AccordionContent>
                                </AccordionItem>
                            </motion.div>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
} 